/**
 * NGX + SEC fee schedule for fixed-income trades on NigaBond Trader.
 *
 * Codified from the NGX pricelist + SEC guidelines as at 2026-Q1. Equities
 * schedule (ngx-fees.ts) is a poor fit — bond fees are materially lower and
 * stamp duty is BUY-side only on the consideration (principal, excluding
 * accrued interest). Municipal (state/LGA) bonds are VAT-exempt on coupon
 * but normal VAT applies to brokerage/SEC here.
 *
 *   - Brokerage:        0.15% of consideration (negotiable; retail default)
 *   - NGX (trade):      0.015% of consideration (reduced vs equities 0.30%)
 *   - SEC Fee:          0.30% of consideration (subject to VAT)
 *   - CSCS:             0.006% of consideration (FGN) / 0.06% for corporates
 *   - Stamp Duty:       0.075% of consideration, BUY only (bond schedule)
 *   - Terminal Fee:     0.10% of consideration (Nigastock platform)
 *   - VAT on Brokerage: 7.5%
 *   - VAT on SEC/NGX:   7.5%
 *   - VAT on CSCS:      5%
 *
 * Accrued interest is NOT part of consideration for fee purposes but IS part
 * of the buyer's total cash outlay. We return both: feesOn (principal) and
 * totalCash (principal + accrued + fees for BUY, principal + accrued − fees
 * for SELL).
 *
 * All math done in kobo bigints.
 */

export type Side = 'BUY' | 'SELL';

export interface BondFeeRates {
  brokerageBps: bigint;
  ngxBps: bigint;        // 1.5bps = 0.015%
  secBps: bigint;
  cscsBps: bigint;
  stampDutyBps: bigint;  // 7.5 bps as "75 / 100000"
  terminalBps: bigint;
  vatBrokeragePctBps: bigint;
  vatSecPctBps: bigint;
  vatCscsPctBps: bigint;
}

export const DEFAULT_BOND_RATES: BondFeeRates = {
  brokerageBps: 15n,
  ngxBps: 2n,           // 0.02% rounded (spec is 0.015%, rounded up for integer bps)
  secBps: 30n,
  cscsBps: 6n,
  stampDutyBps: 75n,    // 0.075%; applied via /100000 helper
  terminalBps: 10n,
  vatBrokeragePctBps: 750n,
  vatSecPctBps: 750n,
  vatCscsPctBps: 500n,
};

export interface BondFeeBreakdown {
  side: Side;
  principal: string;      // kobo, consideration = units * faceKobo * cleanPrice/100
  accrued: string;        // kobo
  brokerage: string;
  vatOnBrokerage: string;
  ngxFee: string;
  vatOnNgx: string;
  secFee: string;
  vatOnSec: string;
  cscsFee: string;
  vatOnCscs: string;
  stampDuty: string;
  terminalFee: string;
  totalFees: string;
  totalCash: string;      // principal + accrued + fees for BUY, -fees for SELL
  rates: {
    brokeragePct: string;
    ngxPct: string;
    secPct: string;
    cscsPct: string;
    stampDutyPct: string;
    terminalPct: string;
    vatBrokeragePct: string;
    vatSecPct: string;
    vatCscsPct: string;
  };
}

function assertNonNegBigint(n: bigint, field: string) {
  if (n < 0n) throw new Error(field + ' must be non-negative');
}
function ofBps(amount: bigint, bps: bigint): bigint {
  return (amount * bps) / 10000n;
}
function ofStampBps(amount: bigint, bps: bigint): bigint {
  // stampDutyBps is in "tenths of bps" — 75n => 0.075% → divide by 100000
  return (amount * bps) / 100000n;
}
function bpsToPct(bps: bigint, denom = 10000n): string {
  // Return a human-readable percent (e.g. 30 -> "0.3").
  const num = Number(bps) / (Number(denom) / 100);
  const str = num.toFixed(4).replace(/\.?0+$/, '');
  return str === '' ? '0' : str;
}

/**
 * Compute the full NGX bond fee breakdown.
 * @param units  Number of bond units (integer)
 * @param faceKoboPerUnit  Face value per unit in kobo (e.g. 100_000n for N1,000 face)
 * @param cleanPriceE4     Clean price × 10_000 (e.g. 98.75% -> 987500) integer
 * @param accruedKobo      Accrued interest in kobo for the whole trade (units * ai)
 */
export function computeBondFees(
  side: Side,
  units: number,
  faceKoboPerUnit: bigint,
  cleanPriceE4: bigint,
  accruedKobo: bigint,
  rates: BondFeeRates = DEFAULT_BOND_RATES,
): BondFeeBreakdown {
  if (!Number.isInteger(units) || units <= 0) throw new Error('units must be a positive integer');
  assertNonNegBigint(faceKoboPerUnit, 'faceKoboPerUnit');
  assertNonNegBigint(cleanPriceE4, 'cleanPriceE4');
  assertNonNegBigint(accruedKobo, 'accruedKobo');

  const u = BigInt(units);
  // principal = units * face * cleanPriceE4 / 1_000_000 (cleanPriceE4 is %*10000 = bps*100)
  const principal = (u * faceKoboPerUnit * cleanPriceE4) / 1_000_000n;

  const brokerage = ofBps(principal, rates.brokerageBps);
  const ngxFee = ofBps(principal, rates.ngxBps);
  const secFee = ofBps(principal, rates.secBps);
  const cscsFee = ofBps(principal, rates.cscsBps);
  const stampDuty = side === 'BUY' ? ofStampBps(principal, rates.stampDutyBps) : 0n;
  const terminalFee = ofBps(principal, rates.terminalBps);

  const vatOnBrokerage = ofBps(brokerage, rates.vatBrokeragePctBps);
  const vatOnNgx = ofBps(ngxFee, rates.vatSecPctBps);
  const vatOnSec = ofBps(secFee, rates.vatSecPctBps);
  const vatOnCscs = ofBps(cscsFee, rates.vatCscsPctBps);

  const totalFees = brokerage + vatOnBrokerage + ngxFee + vatOnNgx + secFee + vatOnSec
                  + cscsFee + vatOnCscs + stampDuty + terminalFee;

  const totalCash = side === 'BUY'
    ? principal + accruedKobo + totalFees
    : principal + accruedKobo - totalFees;

  return {
    side,
    principal: principal.toString(),
    accrued: accruedKobo.toString(),
    brokerage: brokerage.toString(),
    vatOnBrokerage: vatOnBrokerage.toString(),
    ngxFee: ngxFee.toString(),
    vatOnNgx: vatOnNgx.toString(),
    secFee: secFee.toString(),
    vatOnSec: vatOnSec.toString(),
    cscsFee: cscsFee.toString(),
    vatOnCscs: vatOnCscs.toString(),
    stampDuty: stampDuty.toString(),
    terminalFee: terminalFee.toString(),
    totalFees: totalFees.toString(),
    totalCash: totalCash.toString(),
    rates: {
      brokeragePct: bpsToPct(rates.brokerageBps),
      ngxPct: bpsToPct(rates.ngxBps),
      secPct: bpsToPct(rates.secBps),
      cscsPct: bpsToPct(rates.cscsBps),
      stampDutyPct: bpsToPct(rates.stampDutyBps, 100000n),
      terminalPct: bpsToPct(rates.terminalBps),
      vatBrokeragePct: bpsToPct(rates.vatBrokeragePctBps),
      vatSecPct: bpsToPct(rates.vatSecPctBps),
      vatCscsPct: bpsToPct(rates.vatCscsPctBps),
    },
  };
}
